/**
 * Apollo Cache Invalidation Utilities
 *
 * Utilities for invalidating Apollo cache after REST mutations.
 * This bridges TanStack Query mutations with Apollo cache management.
 *
 * Feature: 063-graphql-frontend
 */

import { ApolloClient, useApolloClient as useApolloClientOriginal } from '@apollo/client';

/**
 * Safe version of useApolloClient that returns null when not in an ApolloProvider
 * This allows hooks to work both with and without GraphQL integration
 */
export function useApolloClientSafe(): ApolloClient<unknown> | null {
  try {
    return useApolloClientOriginal();
  } catch {
    // Not inside an ApolloProvider - this is fine for components that don't use GraphQL
    return null;
  }
}

/**
 * Known GraphQL query operation names
 */
export type QueryName =
  // Collections
  | 'GetCollections'
  | 'GetCollection'
  | 'GetCollectionPhotos'
  // Photos
  | 'GetPhotos'
  | 'GetPhoto'
  | 'GetPhotoBySlug'
  | 'GetPhotoMetadata'
  // Critiques
  | 'GetCritiquesForPhoto'
  | 'GetCritiquesForCollection'
  | 'GetCollectionPhotosCritiques'
  | 'GetCritiqueEdits'
  // Sessions
  | 'GetCritiqueSessions'
  | 'GetCritiqueSession'
  // Users
  | 'GetUser'
  | 'GetUsers'
  // Notifications
  | 'GetNotifications'
  // Tracking
  | 'GetActivities'
  | 'GetCheckpoint'
  // Tasks (Feature: 078-user-tasks, 648-task-triggers)
  | 'GetTask'
  | 'GetTasks'
  | 'GetTaskSummary';

/**
 * Mapping of mutation types to the queries they should invalidate
 */
export const MUTATION_INVALIDATION_MAP: Record<string, QueryName[]> = {
  // Collection mutations
  createCollection: ['GetCollections'],
  updateCollection: ['GetCollection', 'GetCollections'],
  deleteCollection: ['GetCollections', 'GetCritiqueSessions'],
  addPhotoToCollection: ['GetCollection', 'GetCollectionPhotos'],
  removePhotoFromCollection: ['GetCollection', 'GetCollectionPhotos'],
  reorderCollectionPhotos: ['GetCollection', 'GetCollectionPhotos'],
  updateCollectionItem: ['GetCollection', 'GetCollectionPhotos'],

  // Photo mutations
  uploadPhoto: ['GetPhotos'],
  createPhoto: ['GetPhotos'],
  deletePhoto: ['GetPhotos', 'GetCollection', 'GetCollectionPhotos'],
  // Feature 081: photo-versioning - New version updates photo data
  createPhotoVersion: ['GetPhotos', 'GetPhoto', 'GetCollection', 'GetCollectionPhotos'],
  generateAICritique: ['GetCritiquesForPhoto'],

  // Critique mutations
  // Feature 648: Critiques with @mentions can create/update/delete tasks, so invalidate task queries
  submitCritique: ['GetCritiquesForCollection'],
  createCritique: ['GetCritiquesForPhoto', 'GetCritiquesForCollection', 'GetCollectionPhotosCritiques', 'GetTasks', 'GetTaskSummary'],
  updateCritique: ['GetCritiquesForPhoto', 'GetCritiquesForCollection', 'GetCollectionPhotosCritiques', 'GetTasks', 'GetTaskSummary'],
  deleteCritique: ['GetCritiquesForPhoto', 'GetCritiquesForCollection', 'GetCollectionPhotosCritiques', 'GetTasks', 'GetTaskSummary'],

  // Edit mutations
  createEdit: ['GetCritiqueEdits', 'GetCritiquesForPhoto'],
  updateEdit: ['GetCritiqueEdits', 'GetCritiquesForPhoto'],
  deleteEdit: ['GetCritiqueEdits', 'GetCritiquesForPhoto'],
  createCritiqueEdit: ['GetCritiqueEdits', 'GetCritiquesForPhoto', 'GetCritiquesForCollection'],
  updateCritiqueEdit: ['GetCritiqueEdits', 'GetCritiquesForPhoto', 'GetCritiquesForCollection'],
  deleteCritiqueEdit: ['GetCritiqueEdits', 'GetCritiquesForPhoto', 'GetCritiquesForCollection'],

  // Session mutations
  createSession: ['GetCritiqueSessions'],
  updateSession: ['GetCritiqueSession', 'GetCritiqueSessions'],
  deleteSession: ['GetCritiqueSessions'],
  createCritiqueSession: ['GetCritiqueSessions', 'GetCollections'],
  updateCritiqueSession: ['GetCritiqueSession', 'GetCritiqueSessions'],
  deleteCritiqueSession: ['GetCritiqueSessions', 'GetCollections'],

  // Notification mutations
  markNotificationRead: ['GetNotifications'],
  markAllNotificationsRead: ['GetNotifications'],
  createNotification: ['GetNotifications'],

  // Tracking mutations
  trackActivities: ['GetActivities'],
  updateCheckpoint: ['GetCheckpoint'],

  // Admin mutations
  adminCreateUser: ['GetUsers'],
  adminUpdateUser: ['GetUser', 'GetUsers'],
  adminVerifyUser: ['GetUser', 'GetUsers'],
  adminUnverifyUser: ['GetUser', 'GetUsers'],
  adminDeleteUser: ['GetUsers'],
  adminGenerateTestUser: ['GetUsers'],

  // Task mutations (Feature: 078-user-tasks, 648-task-triggers)
  createTask: ['GetTasks', 'GetTaskSummary'],
  updateTaskStatus: ['GetTask', 'GetTasks', 'GetTaskSummary'],
  updateTaskProgress: ['GetTask', 'GetTasks', 'GetTaskSummary'],
  completeAllTasks: ['GetTasks', 'GetTaskSummary'],
  dismissAllTasks: ['GetTasks', 'GetTaskSummary'],
  deleteTask: ['GetTasks', 'GetTaskSummary'],
};

/**
 * Invalidate Apollo cache for specific queries
 *
 * @param client - Apollo Client instance
 * @param queries - Array of query names to invalidate
 *
 * @example
 * ```typescript
 * const apolloClient = useApolloClient();
 * await invalidateApolloQueries(apolloClient, ['GetCollections', 'GetCollection']);
 * ```
 */
export async function invalidateApolloQueries(
  client: ApolloClient<unknown>,
  queries: QueryName[]
): Promise<void> {
  if (queries.length === 0) return;

  // Evict root query fields to force refetch
  const queryFieldMap: Record<string, string[]> = {
    GetCritiquesForPhoto: ['critiquesForPhoto'],
    GetCritiquesForCollection: ['critiquesForCollection'],
    GetCollectionPhotosCritiques: ['collection'],
    GetCritiqueEdits: ['critiqueEdits'],
    GetCritiqueSessions: ['critiqueSessions'],
    GetCritiqueSession: ['critiqueSession'],
    GetCollections: ['collections', 'userCollections'],
    GetCollection: ['collection'],
    GetCollectionPhotos: ['collectionPhotos'],
    GetPhotos: ['photos', 'userPhotos'],
    GetPhoto: ['photo'],
    GetPhotoBySlug: ['photoBySlug'],
    GetPhotoMetadata: ['photoMetadata'],
    GetUser: ['user'],
    GetUsers: ['users'],
    GetNotifications: ['notifications', 'unreadNotifications'],
    GetActivities: ['activities', 'userActivities'],
    GetCheckpoint: ['checkpoint', 'userCheckpoint'],
    // Tasks (Feature: 078-user-tasks, 648-task-triggers)
    GetTask: ['task'],
    GetTasks: ['tasks'],
    GetTaskSummary: ['taskSummary'],
  };

  queries.forEach(queryName => {
    const fields = queryFieldMap[queryName];
    if (fields) {
      fields.forEach(field => {
        client.cache.evict({ fieldName: field });
      });
    }
  });

  // Run garbage collection to clean up orphaned references
  client.cache.gc();

  // Also trigger refetch for any active queries watching these fields
  await client.refetchQueries({
    include: queries,
  });
}

/**
 * Invalidate Apollo cache based on mutation type
 *
 * @param client - Apollo Client instance
 * @param mutationType - The mutation type (e.g., 'createCollection')
 *
 * @example
 * ```typescript
 * const apolloClient = useApolloClient();
 * await invalidateForMutation(apolloClient, 'createCollection');
 * ```
 */
export async function invalidateForMutation(
  client: ApolloClient<unknown>,
  mutationType: keyof typeof MUTATION_INVALIDATION_MAP
): Promise<void> {
  const queries = MUTATION_INVALIDATION_MAP[mutationType];
  if (queries) {
    await invalidateApolloQueries(client, queries);
  }
}

/**
 * Evict a specific object from the cache
 *
 * @param client - Apollo Client instance
 * @param typename - GraphQL type name (e.g., 'Collection', 'Photo')
 * @param id - Object ID
 *
 * @example
 * ```typescript
 * const apolloClient = useApolloClient();
 * evictObject(apolloClient, 'Collection', 'co4m');
 * ```
 */
export function evictObject(
  client: ApolloClient<unknown>,
  typename: string,
  id: string
): void {
  client.cache.evict({
    id: client.cache.identify({ __typename: typename, id }),
  });
  client.cache.gc();
}

/**
 * Evict all objects of a specific type from the cache
 *
 * @param client - Apollo Client instance
 * @param typename - GraphQL type name
 *
 * @example
 * ```typescript
 * const apolloClient = useApolloClient();
 * evictAllOfType(apolloClient, 'Collection');
 * ```
 */
export function evictAllOfType(
  client: ApolloClient<unknown>,
  typename: string
): void {
  client.cache.evict({
    fieldName: typename.toLowerCase() + 's',
  });
  client.cache.gc();
}

/**
 * Modify a specific field in the cache
 *
 * @param client - Apollo Client instance
 * @param typename - GraphQL type name
 * @param id - Object ID
 * @param field - Field name to modify
 * @param modifier - Function to modify the field value
 *
 * @example
 * ```typescript
 * const apolloClient = useApolloClient();
 * modifyField(apolloClient, 'Collection', 'co4m', 'title', () => 'New Title');
 * ```
 */
export function modifyField(
  client: ApolloClient<unknown>,
  typename: string,
  id: string,
  field: string,
  modifier: (existingValue: unknown) => unknown
): void {
  client.cache.modify({
    id: client.cache.identify({ __typename: typename, id }),
    fields: {
      [field]: modifier,
    },
  });
}

/**
 * Reset the entire Apollo cache
 *
 * @param client - Apollo Client instance
 *
 * @example
 * ```typescript
 * const apolloClient = useApolloClient();
 * await resetCache(apolloClient);
 * ```
 */
export async function resetCache(
  client: ApolloClient<unknown>
): Promise<void> {
  await client.resetStore();
}
